/**
 * Phase 3: deterministic expert-result reuse (backend AI.expert_reuse).
 *
 * A result row carries `metadata.reuse` when its expert declares a refresh policy:
 * reused rows name their source result/run, the original evidence time and why reuse
 * was valid; fresh rows say whether they can be reused later. Read-only presentation.
 */

export interface RefreshPolicy {
  timeframe?: string;
  max_age_seconds?: number;
}

export interface ReuseRecord {
  reused?: boolean;
  eligible?: boolean;
  reasons?: string[];
  source_result_id?: string;
  source_execution_id?: string | null;
  evidence_as_of?: string;
  reused_at?: string;
  evidence_age_seconds?: number;
  refresh_policy?: RefreshPolicy | null;
  completed_bars?: Record<string, string | null>;
  reason?: string;
  model_cost_avoided?: number;
  lookup_skipped?: string;
}

export const formatAge = (seconds?: number): string => {
  if (seconds == null || !Number.isFinite(seconds)) return 'unknown';
  if (seconds % 86400 === 0 && seconds >= 86400) return `${seconds / 86400}d`;
  if (seconds >= 3600) return `${Math.round((seconds / 3600) * 10) / 10}h`;
  return `${Math.max(1, Math.round(seconds / 60))}m`;
};

export const describeRefreshPolicy = (policy?: RefreshPolicy | null): string =>
  policy?.timeframe
    ? `On each new completed ${policy.timeframe} bar (safety ceiling: never reused beyond ${formatAge(policy.max_age_seconds)})`
    : 'Every run';

export const getReuse = (result: { metadata?: Record<string, any> | null }): ReuseRecord | null => {
  const reuse = result?.metadata?.reuse;
  return reuse && typeof reuse === 'object' ? (reuse as ReuseRecord) : null;
};

/** Label/detail for the result's cache-or-reuse cell, or null when the expert declares no refresh policy. */
export const getReuseDisplay = (
  result: { metadata?: Record<string, any> | null },
): { label: string; detail: string; reused: boolean } | null => {
  const reuse = getReuse(result);
  if (!reuse) return null;
  if (reuse.reused) {
    const cost = reuse.model_cost_avoided ? ` · model cost avoided $${reuse.model_cost_avoided.toFixed(4)}` : '';
    const evidence = reuse.evidence_as_of ? new Date(reuse.evidence_as_of).toLocaleString() : 'unknown time';
    return {
      reused: true,
      label: 'Reused',
      detail: `Source result ${reuse.source_result_id ?? '?'}${reuse.source_execution_id ? ` (run ${reuse.source_execution_id})` : ''}`
        + ` · evidence as of ${evidence} · ${describeRefreshPolicy(reuse.refresh_policy)}${cost}`,
    };
  }
  if (reuse.eligible === false) {
    return { reused: false, label: 'Runs every time', detail: (reuse.reasons ?? []).join('; ') || 'Not eligible for reuse' };
  }
  return {
    reused: false,
    label: 'Fresh',
    detail: `${describeRefreshPolicy(reuse.refresh_policy)}${reuse.lookup_skipped ? ' · test/rerun runs never reuse' : ''}`,
  };
};
